import { CheckOutlined, CloseOutlined } from '@mui/icons-material'
import {
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  Typography,
  useTheme,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { ClockIcon } from '../../assets/icons/icons'
import moment from 'moment-timezone'
import { useUpdateEmployeeAttendance } from './hooks'
import { useGetTNMItems } from '../projects/time-materials/hooks'
import { useGetAllCostCodes } from '../projects/cost-codes/hooks'
import { CostCodeSelectField } from '../../components/timesheet/CostCodeSelectField'
import { useGetEntityFormByType } from '../../hooks/entityFormHook'
import DateField from '../../components/fields/DateField'
import TimeField from '../../components/fields/TimeField'
import dayjs from 'dayjs'

interface FormData {
  startDate?: any
  startTime?: any
  endDate?: any
  endTime?: any
  isLunchBreakTaken?: boolean
}

function EditTimesheetModel({ data }: { data: any }) {
  const [open, setOpen] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    isLunchBreakTaken: true,
  })
  const [hasErrors, setHasErrors] = useState<String[]>([])
  const [costCodeErrors, setCostCodeErrors] = useState<boolean>(false)
  const [durationIsValid, setDurationIsValid] = useState(true)
  const handleOpen = () => setOpen(true)
  const handleClose = () => setOpen(false)
  const [formattedTime, setFormattedTime] = useState('')
  const [costCodes, setCostCodes] = useState([])
  console.log('🚀 ~ EditTimesheetModel ~ costCodes:', costCodes)
  const [isEditEndTime, setIsEditEndTime] = useState(false)
  const { mutate: mutateUpdate } = useUpdateEmployeeAttendance(
    'update-employee-attendance-reports'
  )
  const [hrQuestions] = useState([])
  const { data: tnmItems } = useGetTNMItems('tmreports', data?.project?._id)
  const { data: costCodeOptions } = useGetAllCostCodes(
    'costcodes',
    data?.project?._id
  )

  useEffect(() => {
    setFormData({
      startDate: dayjs(data?.formattedTimeDurations?.startTime).format(
        'MM/DD/YYYY'
      ),
      startTime: dayjs(data?.formattedTimeDurations?.startTime).format(
        'hh:mm A'
      ),
      endDate: dayjs(data?.formattedTimeDurations?.endTime).format(
        'MM/DD/YYYY'
      ),
      endTime: dayjs(data?.formattedTimeDurations?.endTime).format('hh:mm A'),
      isLunchBreakTaken: data?.isLunchBreakTaken,
    })
  }, [open, data])

  useEffect(() => {
    setFormattedTime(getTimeDifference())
  }, [formData])

  function getTimeDifference() {
    const startDate = formData?.startDate
    const endDate = formData?.endDate
    const start = moment(`${startDate} ${formData?.startTime}`)
    const end = moment(`${endDate} ${formData?.endTime}`)
    const duration = moment.duration(end?.diff(start))
    const hours = Math.floor(duration?.asHours())
    const minutes = duration?.minutes()
    const totalTime = `${hours > 0 ? hours + 'h ' : ''}${
      minutes > 0 ? minutes + 'm' : ''
    }`
    setFormattedTime(totalTime)

    if (hours > 0 || minutes > 0) {
      setDurationIsValid(true)
    } else {
      setDurationIsValid(false)
    }

    return totalTime
  }

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    maxHeight: '85vh',
    overflow: 'scroll',
    p: 2,
    outline: 'none',
  }

  const handleLunchTaken = (e: any) => {
    setFormData({ ...formData, isLunchBreakTaken: e.target.value === 'true' })
  }

  const handleDateTimeChange = (
    fieldName: string,
    fieldValue: string,
    hasError: boolean
  ) => {
    if (fieldName === 'startDate') {
      setFormData({ ...formData, startDate: fieldValue })
    } else if (fieldName === 'endDate') {
      setFormData({ ...formData, endDate: fieldValue })
    } else if (fieldName === 'startTime') {
      setFormData({ ...formData, startTime: fieldValue })
    } else if (fieldName === 'endTime') {
      setIsEditEndTime(true)
      setFormData({ ...formData, endTime: fieldValue })
    }
    let errorsArray = hasErrors.slice() // returns a copy of hasErrors array
    if (hasError && errorsArray.indexOf(fieldName) === -1) {
      errorsArray.push(fieldName)
    } else if (!hasError) {
      const errorIndex = errorsArray.indexOf(fieldName)
      if (errorIndex !== -1) {
        // Remove the fieldName from errorsArray
        errorsArray.splice(errorIndex, 1)
      }
    }
    setHasErrors(errorsArray)
  }

  const handleUpdate = () => {
    const startDate = moment(formData?.startDate?.toString()).format(
      'YYYY-MM-DD'
    )
    const endDate = moment(formData?.endDate?.toString()).format('YYYY-MM-DD')
    const start = moment(
      `${startDate} ${formData?.startTime}`,
      'YYYY-MM-DD hh:mm A'
    )
    const end = moment(`${endDate} ${formData?.endTime}`, 'YYYY-MM-DD hh:mm A')

    const duration = moment.duration(end?.diff(start))
    mutateUpdate({
      projectId: data?._id,
      payload: {
        startTimeStamp: start?.format('MM/DD/YYYY h:mm A'),
        endTimeStamp: end?.format('MM/DD/YYYY h:mm A'),
        project: data?.project?._id,
        costCodes: costCodes,
        clockOutAnswers: hrQuestions,
        dailyEndTime: data?.dailyEndTime,
        totalTimeWorkedInMinutes: duration?.asMinutes(),
        totalTimeWorkedInHours: duration?.asHours(),
        isLunchBreakTaken: formData?.isLunchBreakTaken,
        lunchBreakTakenByUser: formData?.isLunchBreakTaken,
      },
    })
    handleClose()
  }

  return (
    <>
      <Button
        onClick={handleOpen}
        sx={{
          padding: '1.5px 12px',
          textTransform: 'capitalize',
          borderRadius: 2,
          boxShadow: 'none',
          minWidth: 0,
          backgroundColor: '#24212C99',
          '&:hover': {
            backgroundColor: '#24212C99',
            boxShadow: 'none',
          },
          fontSize: 13,
        }}
        variant='contained'>
        Edit
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby='modal-title'
        aria-describedby='modal-description'>
        <Box sx={modalStyle}>
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}>
            <Typography id='modal-title' variant='h6' component='h2'>
              Edit Timesheet
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseOutlined />
            </IconButton>
          </Box>
          <Box
            display={'flex'}
            alignItems={'center'}
            justifyContent={'space-between'}>
            <Typography sx={{ fontSize: 14, marginTop: 1, marginBottom: 1 }}>
              <span style={{ color: '#5F5C5F' }}>Name:</span>{' '}
              {data?.user?.firstName} {data?.user?.lastName}
            </Typography>
          </Box>
          {/* Body Goes Here Inside */}
          <Typography sx={{ fontSize: 14, marginTop: 1 }}>
            <span style={{ color: '#5F5C5F' }}>Start Date and Time:</span>
          </Typography>
          <Box
            display={'flex'}
            alignItems={'flex-start'}
            justifyContent={'center'}
            gap={1}>
            {/* Start Date and Time */}
            <Box
              sx={{
                width: '100%',
              }}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}>
              <DateField
                name='startDate'
                value={formData.startDate}
                update={handleDateTimeChange}
              />
            </Box>
            <Box
              sx={{
                width: '100%',
              }}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}>
              <TimeField
                name='startTime'
                value={formData.startTime}
                update={handleDateTimeChange}
              />
            </Box>
          </Box>
          <Typography sx={{ fontSize: 14, marginTop: 1 }}>
            <span style={{ color: '#5F5C5F' }}>End Date and Time:</span>
          </Typography>
          <Box
            display={'flex'}
            alignItems={'flex-start'}
            justifyContent={'center'}
            gap={1}>
            {/* End Date and Time */}
            <Box
              sx={{
                width: '100%',
              }}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}>
              <DateField
                name='endDate'
                value={formData.endDate}
                update={handleDateTimeChange}
              />
            </Box>
            <Box
              sx={{
                width: '100%',
              }}
              display={'flex'}
              alignItems={'center'}
              justifyContent={'center'}>
              <TimeField
                name='endTime'
                value={formData.endTime}
                update={handleDateTimeChange}
              />
            </Box>
          </Box>
          <Box
            sx={{
              width: '100%',
            }}
            display={'flex'}
            alignItems={'center'}>
            <Typography
              fontSize={'14px'}
              color='#5A525B'
              fontWeight='bold'
              sx={{ mr: 2 }}>
              Did the worker take a lunch break?{' '}
            </Typography>
            <RadioGroup
              aria-label='lunch-taken'
              name='isLunchBreakTaken'
              value={formData.isLunchBreakTaken?.toString()}
              row
              onChange={e => {
                handleLunchTaken(e)
              }}>
              <FormControlLabel
                value={'true'}
                control={<Radio />}
                label={'Yes'}
              />
              <FormControlLabel
                value={'false'}
                control={<Radio />}
                label={'No'}
              />
            </RadioGroup>
          </Box>
          <Typography
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'start',
              fontSize: 14,
              marginTop: 1,
              marginBottom: 1,
            }}>
            <ClockIcon /> <Box sx={{ width: 3 }} />
            <span style={{ color: '#5F5C5F' }}>Total Time: </span>
            <Box sx={{ width: 5 }} /> {formattedTime}
            {!durationIsValid && isEditEndTime && (
              <div style={{ color: 'red' }}>
                Total Time must be greater than zero.
              </div>
            )}
          </Typography>

          <Grid>
            {data && data?._id && costCodeOptions ? (
              <CostCodeSelectField
                handleCostCodes={setCostCodes}
                initialCostCodes={
                  data.costCodes && data.costCodes.length
                    ? data.costCodes
                    : [
                        {
                          costCode: '',
                          percentage: 100,
                          tnmItem: null,
                          description: '',
                        },
                      ]
                }
                costCodeOptions={costCodeOptions}
                tnmItems={tnmItems}
                setError={setCostCodeErrors}
                enableDescription={true}
              />
            ) : null}
          </Grid>
          {/* Body Goes Here Inside */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant='contained'
              sx={{
                mr: 1,
                backgroundColor: '#EEEEEE',
                '&:hover': { backgroundColor: '#EEEEEE', boxShadow: 'none' },
                color: '#5A525B',
                textTransform: 'capitalize',
                fontWeight: 'normal',
                boxShadow: 'none',
                px: 3,
                borderRadius: 1.5,
                paddingTop: 1.1,
                paddingBottom: 1.1,
              }}
              onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant='contained'
              disabled={
                hasErrors.length > 0 ||
                !durationIsValid ||
                costCodeErrors ||
                (costCodes.length > 0 &&
                  (costCodes || [])?.filter((item: any) => !item?.costCode)
                    ?.length > 0)
              }
              onClick={handleUpdate}
              sx={{
                width: '100%',
                '&:hover': { boxShadow: 'none' },
                boxShadow: 'none',
                textTransform: 'capitalize',
                paddingTop: 1.1,
                paddingBottom: 1.1,
                borderRadius: 1.5,
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
              }}>
              {/* {isLoadingUpdate ? <CircularProgress sx={{ color: 'white', width: 20 }} size={25} /> : <> */}
              <CheckOutlined sx={{ width: 20, height: 20 }} />{' '}
              <div style={{ width: 5 }} />
              Update
              {/* </>} */}
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default EditTimesheetModel
